import { courseCatalogSlots } from '@/features/catalog/course-catalog/paths'
import { learningPathCatalogSlots } from '@/features/catalog/learning-path-catalog/paths'
import { courseAssignmentDetailsSlot } from '@/features/entitlements/course-assignments/paths'
// The slot names' own module, not the users folder's index, which would pull its chooser into the main bundle.
import { userDetailsSlot, userFormTabsSlot } from '@/features/site-management/users/user'
import { detailsSlots } from '@/shared/details'
import { column, type Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { GraduationCap } from '@/shared/ui/icons'

import { completionsPath } from './completions/paths'
import { deleteCreditsPermission, writeCreditsPermission } from './credits/course-credits-api'
import { myDataApi } from './my-data/paths'
import { myTranscriptPath, readLearnerRecordsPermission, transcriptPath, transcriptRoute } from './transcripts/paths'

/** Learner History: completions, course credits, the transcript, the completion search and the learner's progress. Its screens register here. */
export const feature: Feature = {
  id: 'learner-history',
  routes: [
    // Find Completions (LH-09).
    {
      path: completionsPath,
      titleId: 'learnerHistory.completions.find.title',
      page: () => import('./completions/FindCompletionsPage').then((module) => module.FindCompletionsPage),
    },
    // A person's transcript and My Transcript (LH-08).
    {
      path: transcriptRoute,
      titleId: 'learnerHistory.transcript.title',
      page: () => import('./transcripts/TranscriptPage').then((module) => module.TranscriptPage),
    },
    {
      path: myTranscriptPath,
      titleId: 'learnerHistory.transcript.myTitle',
      page: () => import('./transcripts/TranscriptPage').then((module) => module.MyTranscriptPage),
    },
  ],
  nav: [{ id: 'my-transcript', labelId: 'learnerHistory.transcript.myTitle', path: myTranscriptPath, permission: readLearnerRecordsPermission }],
  adminHome: {
    links: [
      {
        id: 'find-completions',
        category: 'course-assignments',
        labelId: 'learnerHistory.completions.find.title',
        path: completionsPath,
        permission: readLearnerRecordsPermission,
      },
    ],
  },
  slots: [
    {
      // Giving and removing course credits on Create and Edit User, saved with the form (LH-02).
      kind: 'tab',
      slot: userFormTabsSlot,
      id: 'course-credit',
      labelId: 'learnerHistory.credits.tab',
      permission: writeCreditsPermission,
      component: () => import('./credits/CourseCreditFormTab').then((module) => module.CourseCreditFormTab),
    },
    {
      // The person's course credits, with their count, on User Details: either aspect lists them.
      kind: 'tab',
      slot: detailsSlots(userDetailsSlot).tabs,
      id: 'course-credit',
      labelId: 'learnerHistory.credits.tab',
      permission: [writeCreditsPermission, deleteCreditsPermission],
      component: () => import('./credits/CourseCreditsTab').then((module) => module.CourseCreditsTab),
      count: () => import('./credits/CourseCreditsCount').then((module) => module.CourseCreditsCount),
    },
    {
      // The person's transcript, from User Details' side menu (LH-08).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'transcript',
      labelId: 'learnerHistory.transcript.title',
      icon: GraduationCap,
      path: (context) => transcriptPath(context.recordId ?? ''),
    },
    {
      // Mark Complete and Clear Completion on Course Assignment Details (LH-14).
      kind: 'action',
      slot: detailsSlots(courseAssignmentDetailsSlot).actions,
      id: 'completion',
      component: () => import('./completions/CourseAssignmentCompletionAction').then((module) => module.CourseAssignmentCompletionAction),
    },
    // Learner History's part of "Download my data" on My Profile (LH-15).
    { kind: 'link', slot: myDataSlot, id: 'learner-history.my-data', labelId: 'learnerHistory.myData.part', path: myDataApi },
    // The learner's progress: "Started" on the catalog's courses, and a path's step rail (LH-12).
    column<readonly string[]>({
      kind: 'column',
      slot: courseCatalogSlots.progress,
      id: 'learner-history.progress',
      headerId: 'learnerHistory.progress.column',
      // Each row gets its page's ids, which its cell reads once for the page.
      load: (ids) => Promise.resolve(new Map(ids.map((id) => [id, ids]))),
      cell: () => import('./progress/CourseProgressCell').then((module) => module.CourseProgressCell),
    }),
    {
      kind: 'panel',
      slot: learningPathCatalogSlots.progress,
      id: 'learner-history.progress',
      component: () => import('./progress/LearningPathProgressPanel').then((module) => module.LearningPathProgressPanel),
    },
  ],
}
